import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import { 
  CalendarIcon, 
  Plus, 
  Trash,
  Activity, 
  Calendar, 
  HelpCircle, 
  RefreshCw, 
  Heart, 
  FileText
} from "lucide-react";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";

// Define valid interaction types
type InteractionType = "conversation" | "event" | "help" | "reconnection" | "personal" | "note";

const interactionSchema = z.object({
  type: z.string().min(1, "Please select an interaction type"),
  title: z.string().optional(),
  content: z.string().min(1, "Please share what made this moment meaningful"),
  date: z.date({ required_error: "Please select when this happened" }),
});

type InteractionFormValues = z.infer<typeof interactionSchema>;

const AddInteractionForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const form = useForm<InteractionFormValues>({
    resolver: zodResolver(interactionSchema),
    defaultValues: {
      type: undefined,
      title: "",
      content: "",
      date: new Date(),
    },
  });

  // Set focus on content field when form loads
  useEffect(() => {
    if (contentRef.current) {
      setTimeout(() => {
        contentRef.current?.focus();
      }, 100);
    }
  }, []);

  const selectedType = form.watch("type");

  // Get placeholder based on interaction type
  const getPlaceholder = (): string => {
    switch (selectedType) {
      case "conversation": return "What did you talk about? How did it make you feel? Any insights to remember?";
      case "event": return "What made this event special? How did it strengthen your connection?";
      case "help": return "How did you support each other? What impact did this have on your relationship?";
      case "reconnection": return "How did it feel to reconnect? What's changed since you last connected?";
      case "personal": return "What personal moment did you share? How did it deepen your connection?";
      case "note": return "What made this moment meaningful? How do you want to remember it?";
      default: return "What made this moment meaningful?";
    }
  };

  const getInteractionIcon = (type: string) => {
    switch (type) {
      case "conversation": return <Activity className="mr-2 h-4 w-4" />;
      case "event": return <Calendar className="mr-2 h-4 w-4" />;
      case "help": return <HelpCircle className="mr-2 h-4 w-4" />;
      case "reconnection": return <RefreshCw className="mr-2 h-4 w-4" />;
      case "personal": return <Heart className="mr-2 h-4 w-4" />;
      case "note": return <FileText className="mr-2 h-4 w-4" />;
      default: return null;
    }
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles: File[] = [];
    const newPreviews: string[] = [];
    
    Array.from(files).forEach(file => {
      // Check file size (max 5MB per file)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Images must be less than 5MB",
          variant: "destructive",
        });
        return;
      }
      
      newFiles.push(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          newPreviews.push(e.target.result as string);
          
          // Update state when we have all previews
          if (newPreviews.length === newFiles.length) {
            setPhotoFiles([...photoFiles, ...newFiles]);
            setPhotoPreviews([...photoPreviews, ...newPreviews]);
          }
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    const updatedFiles = [...photoFiles];
    const updatedPreviews = [...photoPreviews];
    
    updatedFiles.splice(index, 1);
    updatedPreviews.splice(index, 1);
    
    setPhotoFiles(updatedFiles);
    setPhotoPreviews(updatedPreviews);
  };

  const onSubmit = (values: InteractionFormValues) => {
    // In a real app, we would:
    // 1. Upload the photos to a storage service
    // 2. Save the interaction with the photo URLs to the database
    // 3. Associate it with the correct contact
    
    console.log("Interaction form values:", values);
    console.log("Photos:", photoFiles);
    
    const mockPhotoUrls = photoPreviews;
    
    // Create a new interaction object
    const newInteraction = {
      id: `i${Date.now()}`,
      type: (values.type || "note") as InteractionType,
      title: values.title,
      date: format(values.date, "MMMM d, yyyy"),
      content: values.content,
      photoUrls: mockPhotoUrls,
    };
    
    // In a real app, this would be saved to a database
    console.log("New interaction:", newInteraction);
    
    toast({
      title: "Interaction logged",
      description: "Your memory has been saved",
    });
    
    // Navigate back to contact detail
    navigate(`/contacts/${id}`);
  };

  return (
    <div className="mx-auto max-w-2xl rounded-[16px] bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.05)]">
      <h2 className="mb-6 text-xl font-bold">Capture a moment with this person</h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>What kind of interaction was this?</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select the type of moment" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="conversation" className="flex items-center">
                      <div className="flex items-center">
                        <Activity className="mr-2 h-4 w-4 text-blue-500" />
                        <span>Conversation</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="event" className="flex items-center">
                      <div className="flex items-center">
                        <Calendar className="mr-2 h-4 w-4 text-emerald-500" />
                        <span>Event or Celebration</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="help" className="flex items-center">
                      <div className="flex items-center">
                        <HelpCircle className="mr-2 h-4 w-4 text-purple-500" />
                        <span>Help or Support</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="reconnection" className="flex items-center">
                      <div className="flex items-center">
                        <RefreshCw className="mr-2 h-4 w-4 text-amber-500" />
                        <span>Reconnection</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="personal" className="flex items-center">
                      <div className="flex items-center">
                        <Heart className="mr-2 h-4 w-4 text-rose-500" />
                        <span>Shared Something Personal</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="note" className="flex items-center">
                      <div className="flex items-center">
                        <FileText className="mr-2 h-4 w-4 text-slate-500" />
                        <span>General Note</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>
                  Choose a category to help organize your memories
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>When did this happen?</FormLabel>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Select date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setCalendarOpen(false); // Close the calendar after selection
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormDescription>
                  Select the date when this interaction took place
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Give this moment a name (optional)" {...field} />
                </FormControl>
                <FormDescription>
                  A short title can help you quickly identify this memory later
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Details</FormLabel>
                <FormControl>
                  <Textarea 
                    ref={contentRef}
                    placeholder={getPlaceholder()}
                    className="min-h-32"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Capture what made this moment special so you can reflect on it later
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {/* Photo Upload Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <FormLabel>Add photos or screenshots to remember this better</FormLabel>
              <label htmlFor="interaction-photos" className="cursor-pointer">
                <div className="flex items-center gap-2 text-sm text-primary hover:text-primary/80">
                  <Plus className="h-4 w-4" />
                  <span>Add Images</span>
                </div>
                <input 
                  id="interaction-photos"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
              </label>
            </div>
            <FormDescription>
              Images help bring your memories to life (optional)
            </FormDescription>
            
            {photoPreviews.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                {photoPreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img 
                      src={preview} 
                      alt={`Interaction photo ${index + 1}`} 
                      className="w-full h-32 object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate(`/contacts/${id}`)}
              className="border-[#DDDDDD] hover:bg-gray-50 text-gray-600"
            >
              Cancel
            </Button>
            <Button type="submit">
              Save Memory
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AddInteractionForm;
